import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Menu, Layout } from "antd";
import { logout, fetchApplications } from "../../store/auth/authSlice";
import { buildMenuConfig, LOGOUT_KEY } from "./menuConfig";
import { buildAccessChecker } from "./access";
import { esSupervisorDps } from "../../utils/roles";
import "antd/dist/reset.css"; // si aún no lo tienes
import "./NavBodecom.css";

const { Header } = Layout;

/**
 * Convierte tu config (arbitraria) en items del Menu de Antd v5,
 * filtrando por permisos y asignando disabled cuando aplica.
 */
const toAntdItems = (items, canAccess) =>
    items
        .map((it) => {
            const hasChildren = Array.isArray(it.children) && it.children.length > 0;

            // Oculta nodos sin acceso y sin hijos
            const visible =
                it.key === LOGOUT_KEY ||
                hasChildren ||
                canAccess(it.appName);

            if (!visible) return null;

            const common = {
                key: it.key,
                label: it.label,
                icon: it.icon,
                disabled: it.disabled || (it.path && !canAccess(it.appName)),
            };

            if (hasChildren) {
                const children = toAntdItems(it.children, canAccess).filter(Boolean);
                if (!children.length) return null;
                return { ...common, children };
            }

            return common;
        })
        .filter(Boolean);

/**
 * Deriva selectedKey a partir del pathname (coincidencia por prefijo).
 * Si tu key es exactamente la ruta completa, esto funciona directo.
 */
const deriveSelectedKey = (pathname, allKeys) => {
    if (!pathname) return [];
    // busca coincidencia exacta primero
    if (allKeys.includes(pathname)) return [pathname];
    // prueba por prefijo (e.g. /planes-accion coincide con /planes-accion?tipo=1)
    const found = allKeys.find(k => k !== LOGOUT_KEY && pathname.startsWith(k));
    return found ? [found] : [];
};

const flattenKeys = (items, acc = []) => {
    items.forEach(it => {
        acc.push(it.key);
        if (it.children) flattenKeys(it.children, acc);
    });
    return acc;
};

const NavBodecom = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { user, applications, isAuthenticated } = useSelector((s) => s.auth);
    const isAdmin = user?.username === "admin";
    const esDps = esSupervisorDps(user);

    useEffect(() => {
        if (isAuthenticated && applications.length === 0) {
            dispatch(fetchApplications());
        }
    }, [isAuthenticated, applications.length, dispatch]);

    const userApplications = useMemo(
        () => (applications || []).map((a) => a.app_name),
        [applications]
    );

    const canAccess = useMemo(
        () => buildAccessChecker(userApplications),
        [userApplications]
    );

    const menuConfig = useMemo(() => buildMenuConfig(isAdmin, esDps), [isAdmin, esDps]);
    const menuItems = useMemo(() => toAntdItems(menuConfig, canAccess), [menuConfig, canAccess]);

    // selected/open keys
    const allKeys = useMemo(() => flattenKeys(menuItems), [menuItems]);
    const [selectedKeys, setSelectedKeys] = useState(deriveSelectedKey(location.pathname, allKeys));

    useEffect(() => {
        setSelectedKeys(deriveSelectedKey(location.pathname, allKeys));
    }, [location.pathname, allKeys]);

    const onClick = async (info) => {
        const { key } = info;
        if (key === LOGOUT_KEY) {
            try {
                await dispatch(logout()).unwrap();
                navigate("/");
            } catch (err) {
                console.error("Error durante el logout:", err);
            }
            return;
        }
        // Busca el item para obtener su "path"
        const findPath = (items) => {
            for (const it of items) {
                if (it.key === key && it.path) return it.path;
                if (it.children) {
                    const found = findPath(it.children);
                    if (found) return found;
                }
            }
            return null;
        };
        const path = findPath(menuConfig);
        if (path && path !== "#") navigate(path);
    };

    return (
        <Header className="navbodecom-header">
            <Menu
                mode="horizontal"
                selectedKeys={selectedKeys}
                items={menuItems}
                onClick={onClick}
                style={{ borderBottom: "1px solid #f0f0f0" }}
                className="navbodecom-menu"
            />
        </Header>
    );
};

export default NavBodecom;
